import { useState, useEffect, useRef } from 'react'
import {
  Volume2,
  Mic,
  Play,
  Square,
  ChevronDown,
  Shield,
  ShieldOff,
  Zap,
  AlertCircle,
  RefreshCw
} from 'lucide-react'
import { useToast } from '../../hooks/useToast'
// DFN3 is lazy-loaded only when needed (23MB WASM file!)
// import { startDfn3V2Pipeline } from '../../dfn3/main-v2'

/**
 * Audio & Video Settings Tab - GJYL Dashboard Style
 *
 * Uses DFN3 SDK pattern directly:
 * - startDfn3V2Pipeline() to get processed audio stream
 * - setAttenuation() for noise suppression strength
 * - setBassBoost() for voice enhancement
 *
 * Grid Layout:
 * - Zone A: Devices (2-column grid - Input/Output)
 * - Zone B: Voice Isolation (Noise Suppression Toggle)
 * - Zone C: Mic Check (LED bar visualizer)
 *
 * MICROPHONE ACCESS FLOW:
 * 1. On mount: enumerate devices WITHOUT requesting permission
 * 2. If devices have no labels: show "Grant Permission" button
 * 3. Only call getUserMedia when user explicitly clicks a button
 * 4. Handle specific error types with clear user messages
 */

// ============================================================================
// HELPER: Map MediaDevices errors to user-friendly messages
// ============================================================================
function getMicErrorMessage(error) {
  const errorName = error?.name || 'Unknown'
  const errorMessage = error?.message || ''

  console.error('[VoiceVideoTab] Mic error:', { name: errorName, message: errorMessage, error })

  switch (errorName) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Microphone permission denied. Click the camera icon in your browser\'s address bar to allow access.'

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone detected. Please connect a microphone and try again.'

    case 'NotReadableError':
    case 'TrackStartError':
      return 'Microphone is in use by another application. Please close other apps using the mic (Zoom, Discord, etc.).'

    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'The selected microphone does not support the requested settings. Try selecting a different device.'

    case 'SecurityError':
      return 'Microphone access blocked by browser security settings. Ensure you\'re on HTTPS.'

    case 'AbortError':
      return 'Microphone access was interrupted. Please try again.'

    case 'TypeError':
      return 'Invalid audio configuration. Please refresh and try again.'

    default:
      // Include the actual error for debugging
      if (errorMessage.includes('Permission')) {
        return 'Microphone permission required. Please allow access when prompted.'
      }
      return `Microphone error: ${errorName}. ${errorMessage}`
  }
}

// Custom dropdown component
function DeviceDropdown({ value, onChange, options, label, disabled }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = options.find(o => o.value === value) || options[0]

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2 px-4 py-3
          bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50
          rounded-xl text-left transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isOpen ? 'border-indigo-500/50 ring-2 ring-indigo-500/20' : ''}
        `}
      >
        <span className="text-sm text-white truncate">
          {selectedOption?.label || 'Select device'}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 py-1 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value)
                setIsOpen(false)
              }}
              className={`
                w-full px-4 py-2.5 text-left text-sm transition-colors
                ${option.value === value
                  ? 'bg-indigo-600/20 text-indigo-300'
                  : 'text-gray-300 hover:bg-gray-700/60 hover:text-white'
                }
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Volume slider component
function VolumeSlider({ value, onChange, label }) {
  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-gray-400">{label}</span>
        <span className="text-xs text-gray-400 font-mono">{value}%</span>
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
          [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
          [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
          [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
          [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
        style={{
          background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${value}%, #374151 ${value}%, #374151 100%)`
        }}
      />
    </div>
  )
}

export default function VoiceVideoTab({ profile, onUpdateProfile }) {
  const toast = useToast()

  // Device states
  const [inputDevices, setInputDevices] = useState([])
  const [outputDevices, setOutputDevices] = useState([])
  const [selectedInputDevice, setSelectedInputDevice] = useState('default')
  const [selectedOutputDevice, setSelectedOutputDevice] = useState('default')
  const [devicesLoading, setDevicesLoading] = useState(true)
  const [needsPermission, setNeedsPermission] = useState(false)
  const [permissionState, setPermissionState] = useState('unknown') // 'unknown' | 'prompt' | 'granted' | 'denied'

  // Volume states
  const [inputVolume, setInputVolume] = useState(80)
  const [outputVolume, setOutputVolume] = useState(100)

  // Noise suppression states
  // Default: OFF on mobile (to avoid 23MB WASM download), ON for desktop
  const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState(() => {
    const saved = localStorage.getItem('gjyl_noise_suppression')
    if (saved !== null) return saved === 'true'
    // Default based on device - mobile OFF, desktop ON
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return !isMobile
  })

  // DFN3 strength: 0-100%, default 50%. Maps to attenuation 0.0-1.5
  const [dfn3Strength, setDfn3Strength] = useState(() => {
    const saved = localStorage.getItem('gjyl_dfn3_strength')
    return saved !== null ? parseInt(saved) : 50 // Default 50% (balanced)
  })

  // Bass boost: 0-100%, default 50%. Maps to boost 0.5-2.0
  const [bassBoost, setBassBoost] = useState(() => {
    const saved = localStorage.getItem('gjyl_bass_boost')
    return saved !== null ? parseInt(saved) : 50 // Default 50% (1.0x = no boost)
  })

  // Voice volume: 0-200%, default 100%. Maps to gain 0.0-2.0
  const [voiceVolume, setVoiceVolume] = useState(() => {
    const saved = localStorage.getItem('gjyl_voice_volume')
    return saved !== null ? parseInt(saved) : 100 // Default 100% (1.0x = normal)
  })

  const [processorLoading, setProcessorLoading] = useState(false)
  const [processorError, setProcessorError] = useState(null)
  const [processorStatus, setProcessorStatus] = useState('')

  // Mic test states
  const [isTesting, setIsTesting] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0)
  const [noiseReduction, setNoiseReduction] = useState(0) // LSNR-based noise reduction meter (0-100%)

  // Refs
  const dfn3PipelineRef = useRef(null)  // DFN3 SDK pipeline handles
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const playbackAudioRef = useRef(null)
  const animationFrameRef = useRef(null)
  const isTestingRef = useRef(false)

  // Save noise suppression preferences
  useEffect(() => {
    localStorage.setItem('gjyl_noise_suppression', String(noiseSuppressionEnabled))
    localStorage.setItem('gjyl_dfn3_strength', String(dfn3Strength))
    localStorage.setItem('gjyl_bass_boost', String(bassBoost))
    localStorage.setItem('gjyl_voice_volume', String(voiceVolume))
  }, [noiseSuppressionEnabled, dfn3Strength, bassBoost, voiceVolume])

  // Restart mic test when voice isolation toggle changes (while test is running)
  const prevNoiseSuppressionRef = useRef(noiseSuppressionEnabled)
  useEffect(() => {
    if (prevNoiseSuppressionRef.current !== noiseSuppressionEnabled && isTesting) {
      console.log('[VoiceVideoTab] Voice isolation toggled during test, restarting...')
      // Stop and restart to apply the new setting
      stopMicTest().then(() => {
        setTimeout(() => startMicTest(), 100) // Small delay for cleanup
      })
    }
    prevNoiseSuppressionRef.current = noiseSuppressionEnabled
  }, [noiseSuppressionEnabled, isTesting])

  // Apply DFN3 strength changes in real-time
  const handleDfn3StrengthChange = (value) => {
    setDfn3Strength(value)
    // Convert 0-100% to 0.0-1.5 attenuation (capped to avoid artifacts on singing)
    // 0% = 0.0 (no suppression), 50% = 0.75 (balanced), 100% = 1.5 (strong but not harsh)
    const attenuation = (value / 100) * 1.5
    if (dfn3PipelineRef.current?.setAttenuation) {
      dfn3PipelineRef.current.setAttenuation(attenuation)
    }
  }

  // Apply bass boost changes in real-time
  const handleBassBoostChange = (value) => {
    setBassBoost(value)
    // Convert 0-100% to 0.5-2.0 boost
    // 0% = 0.5 (reduce bass), 50% = 1.0 (normal), 100% = 2.0 (double bass)
    const boost = 0.5 + (value / 100) * 1.5
    if (dfn3PipelineRef.current?.setBassBoost) {
      dfn3PipelineRef.current.setBassBoost(boost)
    }
  }

  // Apply voice volume changes in real-time
  const handleVoiceVolumeChange = (value) => {
    setVoiceVolume(value)
    // Convert 0-200% to 0.0-2.0 gain
    // 0% = 0.0 (mute), 100% = 1.0 (normal), 200% = 2.0 (double volume)
    const gain = value / 100
    if (dfn3PipelineRef.current?.setOutputGain) {
      dfn3PipelineRef.current.setOutputGain(gain)
    }
  }

  // ============================================================================
  // PHASE 3 FIX: Load devices on mount WITHOUT requesting permission
  // ============================================================================
  useEffect(() => {
    loadDevicesWithoutPermission()
    checkMicPermissionState()
  }, [])

  /**
   * Check the current permission state via Permissions API
   */
  const checkMicPermissionState = async () => {
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'microphone' })
        console.log('[VoiceVideoTab] Mic permission state:', result.state)
        setPermissionState(result.state)

        // Listen for permission changes
        result.onchange = () => {
          console.log('[VoiceVideoTab] Mic permission changed to:', result.state)
          setPermissionState(result.state)
          if (result.state === 'granted') {
            loadDevicesWithPermission()
          }
        }
      }
    } catch (err) {
      console.log('[VoiceVideoTab] Permissions API not available:', err.message)
    }
  }

  /**
   * Enumerate devices WITHOUT triggering permission prompt
   * If devices have no labels, we need permission (show button to user)
   */
  const loadDevicesWithoutPermission = async () => {
    setDevicesLoading(true)
    console.log('[VoiceVideoTab] Loading devices without permission request...')

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices.filter(d => d.kind === 'audioinput')
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput')

      console.log('[VoiceVideoTab] Found devices:', {
        inputs: audioInputs.length,
        outputs: audioOutputs.length,
        hasLabels: audioInputs.some(d => d.label)
      })

      // Check if we have labels (indicates permission was already granted)
      const hasLabels = audioInputs.some(d => d.label && d.label.length > 0)

      if (hasLabels) {
        // Permission was already granted in a previous session
        setInputDevices(audioInputs)
        setOutputDevices(audioOutputs)
        setNeedsPermission(false)
        setPermissionState('granted')
      } else if (audioInputs.length > 0) {
        // Devices exist but no labels - need permission
        console.log('[VoiceVideoTab] Devices found but no labels - permission needed')
        setInputDevices(audioInputs)
        setOutputDevices(audioOutputs)
        setNeedsPermission(true)
      } else {
        // No devices found at all
        console.log('[VoiceVideoTab] No audio input devices found')
        setNeedsPermission(true)
      }
    } catch (error) {
      console.error('[VoiceVideoTab] Error enumerating devices:', error)
      setNeedsPermission(true)
    }

    setDevicesLoading(false)
  }

  /**
   * Request permission and reload devices with labels
   * Called when user explicitly clicks "Grant Permission" button
   */
  const loadDevicesWithPermission = async () => {
    setDevicesLoading(true)
    console.log('[VoiceVideoTab] Requesting mic permission...')

    try {
      // Request permission with getUserMedia
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Immediately stop the stream - we just needed the permission
      stream.getTracks().forEach(track => track.stop())
      console.log('[VoiceVideoTab] Permission granted, reloading device list...')

      // Now enumerate with labels
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices.filter(d => d.kind === 'audioinput')
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput')

      console.log('[VoiceVideoTab] Devices with labels:', audioInputs.map(d => d.label))

      setInputDevices(audioInputs)
      setOutputDevices(audioOutputs)
      setNeedsPermission(false)
      setPermissionState('granted')

      toast.success('Microphone access granted!')
    } catch (error) {
      const errorMsg = getMicErrorMessage(error)
      console.error('[VoiceVideoTab] Permission request failed:', errorMsg)
      toast.error(errorMsg)

      if (error.name === 'NotAllowedError') {
        setPermissionState('denied')
      }
    }

    setDevicesLoading(false)
  }

  // ============================================================================
  // PHASE 4 FIX: Hardened startMicTest with proper error handling and fallback
  // ============================================================================
  const startMicTest = async () => {
    console.log('[VoiceVideoTab] Starting mic test...')

    // Check if we have permission first
    if (permissionState === 'denied') {
      toast.error('Microphone permission was denied. Please reset permissions in your browser settings.')
      return
    }

    try {
      setIsTesting(true)
      isTestingRef.current = true
      setProcessorLoading(true)
      setProcessorError(null)
      setProcessorStatus('Initializing...')

      let streamToUse = null

      if (noiseSuppressionEnabled) {
        // Attempt DFN3 pipeline first
        try {
          console.log('[VoiceVideoTab] Starting DFN3 noise suppression pipeline...')
          setProcessorStatus('Loading AI noise suppression...')

          // Dynamic import - loads 23MB WASM only when needed (not on page load!)
          const { startDfn3V2Pipeline } = await import('../../dfn3/main-v2')
          const handles = await startDfn3V2Pipeline({
            deviceId: selectedInputDevice !== 'default' ? selectedInputDevice : undefined,
            passthroughOnUnderrun: true,
            outputGain: 0.8,
            onStatus: (msg) => {
              console.log('[VoiceVideoTab] DFN3:', msg)
              setProcessorStatus(msg)
            },
            onModeChange: (mode) => {
              console.log('[VoiceVideoTab] DFN3 mode:', mode)
              if (mode === 'rnnoise') {
                setProcessorError('Using RNNoise fallback')
              } else if (mode === 'bypass') {
                setProcessorError('Bypass mode active')
              }
            },
            onLsnr: (lsnr) => {
              // Convert LSNR to 0-100% noise reduction display
              // LSNR from model: higher = cleaner signal, lower = noisier
              // Actual observed range from logs: -15 (silence/noise) to +5 (speaking)
              //
              // We want: high bar = lots of noise being filtered (model working hard)
              //          low bar = clean signal (little filtering needed)
              //
              // So we invert: low LSNR (noisy) → high bar, high LSNR (clean) → low bar
              const minLsnr = -15  // Silence/background noise
              const maxLsnr = 5    // Speaking/clean signal
              const clampedLsnr = Math.max(minLsnr, Math.min(maxLsnr, lsnr))
              const normalized = (clampedLsnr - minLsnr) / (maxLsnr - minLsnr) // 0-1, where 1 = clean
              const reductionPercent = Math.round((1 - normalized) * 100) // Invert: 0% when clean, 100% when noisy
              setNoiseReduction(reductionPercent)
            }
          })

          // Store the pipeline handles for control
          dfn3PipelineRef.current = handles

          // Apply initial strength, bass boost, and voice volume settings
          // Attenuation capped at 1.5 to avoid artifacts on singing/voice
          const attenuation = (dfn3Strength / 100) * 1.5
          const boost = 0.5 + (bassBoost / 100) * 1.5
          const gain = voiceVolume / 100
          handles.setAttenuation(attenuation)
          handles.setBassBoost(boost)
          handles.setOutputGain(gain)

          // Use the pipeline's audio context and PROCESSED stream for visualization
          // IMPORTANT: Use processedStream (noise-suppressed), NOT stream (raw mic)
          audioContextRef.current = handles.audioContext
          streamToUse = handles.processedStream

          console.log('[VoiceVideoTab] DFN3 pipeline active - using processed stream')
          setProcessorStatus('Voice isolation active')

        } catch (dfn3Error) {
          console.warn('[VoiceVideoTab] DFN3 failed, falling back to raw mic:', dfn3Error)
          setProcessorError('Voice isolation unavailable - using standard mic')
          setProcessorStatus('Using standard microphone...')

          // Fall back to raw mic with built-in browser noise suppression
          streamToUse = await getRawMicStream()
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
        }
      } else {
        // Use raw mic without noise suppression
        console.log('[VoiceVideoTab] Using raw mic (noise suppression disabled)')
        setProcessorStatus('Getting microphone access...')
        streamToUse = await getRawMicStream()
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      }

      setProcessorLoading(false)

      if (!streamToUse) {
        throw new Error('No audio stream available')
      }

      mediaStreamRef.current = streamToUse
      setProcessorStatus('Mic active')

      // Create analyser for visualization (use existing audio context if DFN3 is active)
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      }
      analyserRef.current = audioContextRef.current.createAnalyser()
      analyserRef.current.fftSize = 256

      const source = audioContextRef.current.createMediaStreamSource(streamToUse)
      source.connect(analyserRef.current)

      // Playback for hearing yourself
      // When DFN3 is active, the worklet already plays to audioContext.destination
      // so we only need Audio element playback when using raw mic
      if (!noiseSuppressionEnabled || !dfn3PipelineRef.current) {
        playbackAudioRef.current = new Audio()
        playbackAudioRef.current.srcObject = streamToUse
        playbackAudioRef.current.volume = outputVolume / 100
        await playbackAudioRef.current.play()
        console.log('[VoiceVideoTab] Using Audio element for playback (raw mic)')
      } else {
        console.log('[VoiceVideoTab] Skipping Audio element - DFN3 worklet plays directly to speakers')
      }

      // If we didn't have permission before, we do now
      if (needsPermission) {
        setNeedsPermission(false)
        setPermissionState('granted')
        // Reload device list to get labels
        loadDevicesWithoutPermission()
      }

      visualizeAudio()

    } catch (error) {
      const errorMsg = getMicErrorMessage(error)
      console.error('[VoiceVideoTab] Error starting mic test:', errorMsg)
      toast.error(errorMsg)

      setIsTesting(false)
      isTestingRef.current = false
      setProcessorLoading(false)
      setProcessorStatus('')
    }
  }

  /**
   * Get raw microphone stream with specific device selection
   */
  const getRawMicStream = async () => {
    const constraints = {
      audio: {
        deviceId: selectedInputDevice !== 'default' ? { exact: selectedInputDevice } : undefined,
        echoCancellation: true,
        noiseSuppression: !noiseSuppressionEnabled, // Let browser do NS if DFN3 is off
        autoGainControl: true
      }
    }

    console.log('[VoiceVideoTab] Getting raw mic with constraints:', constraints)
    return await navigator.mediaDevices.getUserMedia(constraints)
  }

  const stopMicTest = async () => {
    console.log('[VoiceVideoTab] Stopping mic test...')
    setIsTesting(false)
    isTestingRef.current = false
    setAudioLevel(0)
    setNoiseReduction(0)
    setProcessorStatus('')
    setProcessorError(null)

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause()
      playbackAudioRef.current.srcObject = null
    }

    // Cleanup DFN3 pipeline using SDK pattern
    if (dfn3PipelineRef.current) {
      try {
        console.log('[VoiceVideoTab] Stopping DFN3 pipeline...')
        dfn3PipelineRef.current.stop()
        dfn3PipelineRef.current = null
      } catch (err) {
        console.warn('[VoiceVideoTab] Error stopping DFN3 pipeline:', err)
      }
    }

    // Cleanup raw mic stream if we weren't using DFN3
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop())
      mediaStreamRef.current = null
    }

    // Close audio context if it wasn't managed by DFN3
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        await audioContextRef.current.close()
      } catch (err) {
        // Ignore - context may already be closed by DFN3 pipeline
      }
    }
    audioContextRef.current = null
    analyserRef.current = null
  }

  const visualizeAudio = () => {
    if (!analyserRef.current || !isTestingRef.current) return

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
    analyserRef.current.getByteFrequencyData(dataArray)

    const average = dataArray.reduce((a, b) => a + b) / dataArray.length
    const normalizedLevel = Math.min((average / 128) * 100, 100)

    setAudioLevel(normalizedLevel)

    if (isTestingRef.current) {
      animationFrameRef.current = requestAnimationFrame(visualizeAudio)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isTesting) stopMicTest()
    }
  }, [])

  // LED Bar Visualizer - Larger and more prominent
  const renderLEDBar = () => {
    const segments = 24
    const activeSegments = Math.floor((audioLevel / 100) * segments)

    return (
      <div className="flex gap-1.5 items-center h-12 p-3 bg-gray-900/50 rounded-xl">
        {Array.from({ length: segments }, (_, i) => {
          const isActive = i < activeSegments
          // Green -> Yellow -> Red gradient
          let color = 'bg-gray-700/60'
          if (isActive) {
            if (i < segments * 0.5) {
              color = 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]'
            } else if (i < segments * 0.75) {
              color = 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]'
            } else {
              color = 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]'
            }
          }

          return (
            <div
              key={i}
              className={`flex-1 h-full rounded transition-all duration-75 ${color}`}
            />
          )
        })}
      </div>
    )
  }

  // Format device options for dropdown
  const inputOptions = [
    { value: 'default', label: 'Default Microphone' },
    ...inputDevices.map(d => ({
      value: d.deviceId,
      label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`
    }))
  ]

  const outputOptions = [
    { value: 'default', label: 'Default Speakers' },
    ...outputDevices.map(d => ({
      value: d.deviceId,
      label: d.label || `Speaker ${d.deviceId.slice(0, 8)}`
    }))
  ]

  return (
    <div className="space-y-8">
      {/* Permission Required Banner */}
      {needsPermission && permissionState !== 'denied' && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-200 font-medium">Microphone permission required</p>
              <p className="text-xs text-amber-200/70 mt-1">
                Grant access to see your available devices and test your microphone.
              </p>
            </div>
            <button
              onClick={loadDevicesWithPermission}
              disabled={devicesLoading}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium text-sm rounded-lg transition-colors disabled:opacity-50"
            >
              {devicesLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
              Grant Access
            </button>
          </div>
        </div>
      )}

      {/* Permission Denied Banner */}
      {permissionState === 'denied' && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-200 font-medium">Microphone access denied</p>
              <p className="text-xs text-red-200/70 mt-1">
                Click the camera/lock icon in your browser's address bar to reset permissions, then refresh this page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Zone A: Devices - 2-column grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Input Device Box */}
        <div className="p-6 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Mic className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-base font-semibold text-white">Input Device</h3>
          </div>

          <DeviceDropdown
            value={selectedInputDevice}
            onChange={setSelectedInputDevice}
            options={inputOptions}
            disabled={devicesLoading}
          />

          <VolumeSlider
            value={inputVolume}
            onChange={setInputVolume}
            label="Input Volume"
          />
        </div>

        {/* Output Device Box */}
        <div className="p-6 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Volume2 className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-base font-semibold text-white">Output Device</h3>
          </div>

          <DeviceDropdown
            value={selectedOutputDevice}
            onChange={setSelectedOutputDevice}
            options={outputOptions}
            disabled={devicesLoading}
          />

          <VolumeSlider
            value={outputVolume}
            onChange={setOutputVolume}
            label="Output Volume"
          />
        </div>
      </div>

      {/* Zone B: Voice Processing - 3-column grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Voice Isolation Box */}
        <div className="p-5 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${noiseSuppressionEnabled ? 'bg-emerald-500/20' : 'bg-gray-700/50'}`}>
                {noiseSuppressionEnabled ? (
                  <Shield className="w-4 h-4 text-emerald-400" />
                ) : (
                  <ShieldOff className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Voice Isolation</h3>
                <p className="text-xs text-gray-500">AI noise suppression</p>
              </div>
            </div>
            <button
              onClick={() => setNoiseSuppressionEnabled(!noiseSuppressionEnabled)}
              className={`
                relative w-11 h-6 rounded-full transition-colors duration-200
                ${noiseSuppressionEnabled ? 'bg-emerald-500' : 'bg-gray-600'}
              `}
            >
              <div className={`
                absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full
                transition-transform duration-200 shadow-sm
                ${noiseSuppressionEnabled ? 'translate-x-5' : 'translate-x-0'}
              `} />
            </button>
          </div>
        </div>

        {/* Bass Boost Box */}
        <div className="p-5 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${noiseSuppressionEnabled ? 'bg-amber-500/20' : 'bg-gray-700/50'}`}>
                <Zap className={`w-4 h-4 ${noiseSuppressionEnabled ? 'text-amber-400' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Bass Boost</h3>
                <p className="text-xs text-gray-500">Voice enhancement</p>
              </div>
            </div>
            <span className={`text-sm font-mono ${noiseSuppressionEnabled ? 'text-amber-400' : 'text-gray-500'}`}>
              {bassBoost}%
            </span>
          </div>

          {/* Bass Slider */}
          <div className={`transition-opacity duration-200 ${noiseSuppressionEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-400">Level</span>
              <span className="text-xs text-gray-500">{bassBoost < 40 ? 'Reduce' : bassBoost > 60 ? 'Boost' : 'Normal'}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={bassBoost}
              onChange={(e) => handleBassBoostChange(parseInt(e.target.value))}
              disabled={!noiseSuppressionEnabled}
              className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
                [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
                [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
                [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
              style={{
                background: noiseSuppressionEnabled
                  ? `linear-gradient(to right, #f59e0b 0%, #f59e0b ${bassBoost}%, #374151 ${bassBoost}%, #374151 100%)`
                  : '#374151'
              }}
            />
            <div className="flex justify-between mt-1 text-[10px] text-gray-600">
              <span>Reduce</span>
              <span>Boost</span>
            </div>
          </div>
        </div>

        {/* Voice Volume Box */}
        <div className="p-5 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${noiseSuppressionEnabled ? 'bg-indigo-500/20' : 'bg-gray-700/50'}`}>
                <Volume2 className={`w-4 h-4 ${noiseSuppressionEnabled ? 'text-indigo-400' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Voice Volume</h3>
                <p className="text-xs text-gray-500">Output boost</p>
              </div>
            </div>
            <span className={`text-sm font-mono ${noiseSuppressionEnabled ? 'text-indigo-400' : 'text-gray-500'}`}>
              {voiceVolume}%
            </span>
          </div>

          {/* Volume Slider */}
          <div className={`transition-opacity duration-200 ${noiseSuppressionEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-400">Level</span>
              <span className="text-xs text-gray-500">{voiceVolume < 80 ? 'Quiet' : voiceVolume > 120 ? 'Loud' : 'Normal'}</span>
            </div>
            <input
              type="range"
              min="0"
              max="200"
              value={voiceVolume}
              onChange={(e) => handleVoiceVolumeChange(parseInt(e.target.value))}
              disabled={!noiseSuppressionEnabled}
              className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
                [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
                [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
                [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
              style={{
                background: noiseSuppressionEnabled
                  ? `linear-gradient(to right, #6366f1 0%, #6366f1 ${voiceVolume / 2}%, #374151 ${voiceVolume / 2}%, #374151 100%)`
                  : '#374151'
              }}
            />
            <div className="flex justify-between mt-1 text-[10px] text-gray-600">
              <span>Quiet</span>
              <span>Loud</span>
            </div>
          </div>
        </div>
      </div>

      {/* Zone C: Microphone Test - More prominent */}
      <div className="p-6 bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-700/30">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isTesting ? 'bg-red-500/20 animate-pulse' : 'bg-indigo-500/20'}`}>
              <Mic className={`w-5 h-5 ${isTesting ? 'text-red-400' : 'text-indigo-400'}`} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">Microphone Test</h3>
              <p className="text-xs text-gray-400">
                {processorStatus || (isTesting ? 'Listening...' : 'Click Start to test your mic')}
              </p>
              {processorError && (
                <p className="text-xs text-amber-400">{processorError}</p>
              )}
            </div>
          </div>
          <button
            onClick={isTesting ? stopMicTest : startMicTest}
            disabled={processorLoading}
            className={`
              flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm
              transition-all duration-200 disabled:opacity-50
              ${isTesting
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-600/20'
              }
            `}
          >
            {processorLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : isTesting ? (
              <>
                <Square className="w-4 h-4" />
                Stop
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Start Test
              </>
            )}
          </button>
        </div>

        {/* Larger LED Bar */}
        <div className="mt-2">
          {renderLEDBar()}
        </div>

        {/* Noise Reduction Meter - only show when DFN3 is active and testing */}
        {isTesting && noiseSuppressionEnabled && dfn3PipelineRef.current && (
          <div className="mt-4 p-3 bg-gray-900/50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-gray-300">Noise Being Filtered</span>
              </div>
              <span className="text-xs text-emerald-400 font-mono">{Math.round(noiseReduction)}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all duration-150 rounded-full"
                style={{ width: `${noiseReduction}%` }}
              />
            </div>
            <p className="text-[10px] text-gray-500 mt-2 text-center">
              {noiseReduction > 50 ? 'High noise environment detected' : noiseReduction > 20 ? 'Moderate noise filtering' : 'Clean audio signal'}
            </p>
          </div>
        )}

        {isTesting && (
          <p className="text-xs text-gray-500 mt-4 text-center">
            You should hear yourself through your speakers • Toggle Voice Isolation to compare
          </p>
        )}
      </div>

    </div>
  )
}
